import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Wallet,
  RefreshCcw,
  Trash2,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';

const categoryLabels = {
  'Transport': 'Vervoer',
  'Vervoer': 'Vervoer',
  'Uit eten': 'Eten en drinken',
  'Eten & drinken': 'Eten en drinken',
  'Eten en drinken': 'Eten en drinken',
  'Internet TV & Bellen': 'Abonnementen',
  'Hobby sport & vrije tijd': 'Vrije tijd',
  'Sport & Fitness': 'Vrije tijd',
  'Entertainment': 'Vrije tijd',
  'Contanten': 'Kas',
  'Kas': 'Kas',
  'Overig': 'Nog te beoordelen',
};

const categoryColors = {
  'Boodschappen': 'bg-green-100 text-green-700',
  'Eten en drinken': 'bg-orange-100 text-orange-700',
  'Vervoer': 'bg-blue-100 text-blue-700',
  'Wonen': 'bg-purple-100 text-purple-700',
  'Verzekeringen': 'bg-indigo-100 text-indigo-700',
  'Vrije tijd': 'bg-pink-100 text-pink-700',
  'Kleding': 'bg-yellow-100 text-yellow-700',
  'Abonnementen': 'bg-slate-100 text-slate-700',
  'Gezondheid': 'bg-teal-100 text-teal-700',
  'Salaris': 'bg-emerald-100 text-emerald-700',
  'Uitkering': 'bg-sky-100 text-sky-700',
  'Pensioen': 'bg-cyan-100 text-cyan-700',
  'ZZP / Freelance': 'bg-lime-100 text-lime-700',
  'Transfer': 'bg-blue-100 text-blue-700',
  'Kas': 'bg-amber-100 text-amber-700',
  'Bankkosten': 'bg-gray-100 text-gray-700',
  'Goede doelen': 'bg-rose-100 text-rose-700',
  'Nog te beoordelen': 'bg-gray-100 text-gray-700',
};

export default function Transactions() {
  const churchMode = localStorage.getItem('app_profile') === 'church';
  const navigate = useNavigate();
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const lastThreeMonthsStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const [transactions, setTransactions] = useState([]);
  const [cashbook, setCashbook] = useState({ balance: 0, opening_balance: 0, movement_balance: 0, income_total: 0, expense_total: 0, transfer_in_total: 0, count: 0, items: [] });
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [rebuildingCategories, setRebuildingCategories] = useState(false);
  const [resettingCsvImports, setResettingCsvImports] = useState(false);
  const [resettingAllTransactions, setResettingAllTransactions] = useState(false);
  const [savingCashbook, setSavingCashbook] = useState(false);
  const [deletingCashbookEntry, setDeletingCashbookEntry] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCsvHelper, setShowCsvHelper] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [cashbookForm, setCashbookForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    category: 'Nog te beoordelen',
    merchant: '',
    entry_type: 'expense',
  });
  const [cashOpeningBalance, setCashOpeningBalance] = useState('0');
  const [savingCashProfile, setSavingCashProfile] = useState(false);
  const [page, setPage] = useState(1);
  const fileInputRef = useRef(null);
  const itemsPerPage = 15;
  const cashbookCategories = ['Boodschappen', 'Kleding', 'Eten en drinken', 'Vervoer', 'Nog te beoordelen'];

  const getDisplayCategory = (category) => categoryLabels[category] || category || 'Nog te beoordelen';

  const isRealIncomeTransaction = (tx) => {
    if (!tx?.is_income) return false;
    const normalizedCategory = getDisplayCategory(tx?.category);
    return !['Transfer', 'Kas'].includes(normalizedCategory);
  };

  const isRealExpenseTransaction = (tx) => {
    if (tx?.is_income) return false;
    const normalizedCategory = getDisplayCategory(tx?.category);
    return !['Transfer', 'Kas'].includes(normalizedCategory);
  };

  const getDisplayMerchant = (tx) => {
    if (tx?.merchant && tx.merchant.trim() && tx.merchant.trim().toLowerCase() !== 'onbekend') {
      return tx.merchant.trim();
    }
    const description = String(tx?.description || '').trim();
    if (!description) return 'Nog te beoordelen';
    return description
      .replace(/^['"]|['"]$/g, '')
      .split(/\s{2,}| Apple Pay betaling| Contactloze betaling| Referentie:| MCC:/)[0]
      .replace(/^(BCK\*|CCV\*|SumUp\s+\*|TMC\*)/i, '')
      .trim() || 'Nog te beoordelen';
  };

  const getDisplaySubcategory = (tx) => {
    if (!tx?.subcategory) return '';
    if (tx.subcategory === tx.category) return '';
    return tx.subcategory;
  };

  const isTransactionInSelectedPeriod = (tx) => {
    if (selectedPeriod === 'all') return true;
    const txDate = new Date(tx?.date);
    if (Number.isNaN(txDate.getTime())) return false;
    if (selectedPeriod === 'last_3_months') {
      return txDate >= lastThreeMonthsStart && txDate < nextMonthStart;
    }
    return txDate >= currentMonthStart && txDate < nextMonthStart;
  };

  const selectedPeriodLabel = selectedPeriod === 'all'
    ? 'alle zichtbare transacties'
    : selectedPeriod === 'last_3_months'
      ? 'de laatste 3 maanden'
      : now.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txRes, catRes, accountsRes, cashbookRes] = await Promise.all([
        apiClient.get('/transactions?limit=200').catch(() => ({ data: [] })),
        apiClient.get('/transactions/categories').catch(() => ({ data: [] })),
        apiClient.get('/accounts').catch(() => ({ data: [] })),
        apiClient.get('/cashbook?limit=5000').catch(() => ({ data: { balance: 0, opening_balance: 0, movement_balance: 0, income_total: 0, expense_total: 0, transfer_in_total: 0, count: 0, items: [] } })),
      ]);
      setTransactions(txRes.data);
      setCategories(catRes.data);
      setAccounts(accountsRes.data || []);
      const cashbookData = cashbookRes.data || { balance: 0, opening_balance: 0, movement_balance: 0, income_total: 0, expense_total: 0, transfer_in_total: 0, count: 0, items: [] };
      setCashbook(cashbookData);
      setCashOpeningBalance(String(cashbookData.opening_balance ?? 0));
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter((tx) => {
    const technicalImport = ['csv import', 'bank import', 'csv-import'].includes(String(tx.description || '').trim().toLowerCase());
    if (churchMode && technicalImport) return false;
    const matchesPeriod = isTransactionInSelectedPeriod(tx);
    const matchesSearch = tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.merchant?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getDisplaySubcategory(tx).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tx.category === selectedCategory;
    return matchesPeriod && matchesSearch && matchesCategory;
  });

  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const hasTransactions = transactions.length > 0;
  const showGettingStarted = !hasTransactions;
  const showFilteredEmptyState = hasTransactions && filteredTransactions.length === 0;

  const filteredCashbookItems = (cashbook.items || []).filter((entry) => {
    if (selectedPeriod === 'all') return true;
    const entryDate = new Date(entry?.date);
    if (Number.isNaN(entryDate.getTime())) return false;
    if (selectedPeriod === 'last_3_months') {
      return entryDate >= lastThreeMonthsStart && entryDate < nextMonthStart;
    }
    return entryDate >= currentMonthStart && entryDate < nextMonthStart;
  });

  const bankIncome = filteredTransactions
    .filter((tx) => isRealIncomeTransaction(tx))
    .reduce((sum, tx) => sum + tx.amount, 0);
  const bankExpenses = filteredTransactions
    .filter((tx) => isRealExpenseTransaction(tx))
    .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  const cashIncome = filteredCashbookItems
    .filter((entry) => String(entry?.entry_type || '').trim().toLowerCase() === 'income')
    .reduce((sum, entry) => sum + Math.abs(Number(entry?.amount || 0)), 0);
  const cashExpenses = filteredCashbookItems
    .filter((entry) => String(entry?.entry_type || '').trim().toLowerCase() === 'expense')
    .reduce((sum, entry) => sum + Math.abs(Number(entry?.amount || 0)), 0);
  const totalIncome = bankIncome + cashIncome;
  const totalExpenses = bankExpenses + cashExpenses;

  const exportTransactions = () => {
    if (filteredTransactions.length === 0) {
      toast.error('Geen transacties om te exporteren');
      return;
    }

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const headers = ['datum', 'beschrijving', 'merchant', 'categorie', 'bedrag', 'type'];
    const rows = filteredTransactions.map((tx) => [
      tx.date ? new Date(tx.date).toISOString() : '',
      tx.description || '',
      getDisplayMerchant(tx),
      getDisplayCategory(tx.category),
      Number(tx.amount || 0).toFixed(2),
      tx.is_income ? 'inkomsten' : 'uitgaven',
    ]);
    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `transacties-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export gestart');
  };

  const importTransactions = async () => {
    if (!selectedFile) {
      toast.error('Kies eerst een CSV-bestand');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (accounts[0]?.id) {
        formData.append('default_account_id', accounts[0].id);
      }

      const res = await apiClient.post('/transactions/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { imported, updated, failed, errors } = res.data || {};
      if ((imported || 0) === 0 && (failed || 0) > 0 && Array.isArray(errors) && errors.length > 0) {
        const firstError = errors[0];
        toast.error(`Import mislukt op regel ${firstError.line}: ${firstError.error}`);
      } else {
        toast.success(`Import klaar: ${imported || 0} toegevoegd, ${updated || 0} bijgewerkt, ${failed || 0} mislukt`);
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPage(1);
      fetchData();
    } catch (error) {
      console.error('Error importing transactions:', error);
      toast.error(error.response?.data?.detail || 'Importeren mislukt');
    } finally {
      setImporting(false);
    }
  };

  const rebuildCategories = async () => {
    try {
      setRebuildingCategories(true);
      const res = await apiClient.post('/transactions/categorize/rebuild');
      const { updated = 0, unchanged = 0 } = res.data || {};
      toast.success(`Herindeling klaar: ${updated} aangepast, ${unchanged} al goed`);
      setPage(1);
      fetchData();
    } catch (error) {
      console.error('Error rebuilding categories:', error);
      toast.error(error.response?.data?.detail || 'Herindelen mislukt');
    } finally {
      setRebuildingCategories(false);
    }
  };

  const resetTransactions = async (scope) => {
    const isAll = scope === 'all';
    const confirmed = window.confirm(
      isAll
        ? 'Weet je zeker dat je alle transacties wilt verwijderen? Dit is bedoeld als schone herstart.'
        : 'Weet je zeker dat je alle CSV-import transacties wilt verwijderen zodat je opnieuw schoon kunt importeren?'
    );
    if (!confirmed) return;

    try {
      if (isAll) {
        setResettingAllTransactions(true);
      } else {
        setResettingCsvImports(true);
      }
      const res = await apiClient.post('/transactions/reset', { scope });
      const { deleted_transactions = 0, deleted_accounts = 0, reset_accounts = 0 } = res.data || {};
      toast.success(
        `${deleted_transactions} transacties verwijderd, ${deleted_accounts} importrekeningen verwijderd, ${reset_accounts} rekeningen bijgewerkt`
      );
      setPage(1);
      await fetchData();
    } catch (error) {
      console.error('Error resetting transactions:', error);
      toast.error(error.response?.data?.detail || 'Opschonen mislukt');
    } finally {
      if (isAll) {
        setResettingAllTransactions(false);
      } else {
        setResettingCsvImports(false);
      }
    }
  };

  const saveCashbookEntry = async () => {
    const amount = Number(cashbookForm.amount);
    if (!cashbookForm.description.trim()) {
      toast.error('Geef je kasboekregel eerst een korte naam');
      return;
    }
    if (!cashbookForm.date) {
      toast.error('Kies een datum voor deze kasboekregel');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Vul een geldig bedrag in');
      return;
    }

    try {
      setSavingCashbook(true);
      await apiClient.post('/cashbook/entries', {
        ...cashbookForm,
        amount,
      });
      toast.success('Kasboekregel opgeslagen');
      setCashbookForm({
        date: new Date().toISOString().slice(0, 10),
        description: '',
        amount: '',
        category: 'Nog te beoordelen',
        merchant: '',
        entry_type: 'expense',
      });
      fetchData();
    } catch (error) {
      console.error('Error saving cashbook entry:', error);
      toast.error(error.response?.data?.detail || 'Kasboekregel opslaan mislukt');
    } finally {
      setSavingCashbook(false);
    }
  };

  const saveCashbookProfile = async () => {
    const openingBalance = Number(cashOpeningBalance);
    if (!Number.isFinite(openingBalance) || openingBalance < 0) {
      toast.error('Vul een geldig contant beginsaldo in');
      return;
    }
    try {
      setSavingCashProfile(true);
      await apiClient.put('/cashbook/profile', { opening_balance: openingBalance });
      toast.success('Contant beginsaldo opgeslagen');
      fetchData();
    } catch (error) {
      console.error('Error saving cashbook profile:', error);
      toast.error(error.response?.data?.detail || 'Contant beginsaldo opslaan mislukt');
    } finally {
      setSavingCashProfile(false);
    }
  };

  const deleteCashbookEntry = async (entryId) => {
    try {
      setDeletingCashbookEntry(entryId);
      await apiClient.delete(`/cashbook/entries/${entryId}`);
      toast.success('Kasboekregel verwijderd');
      fetchData();
    } catch (error) {
      console.error('Error deleting cashbook entry:', error);
      toast.error(error.response?.data?.detail || 'Kasboekregel verwijderen mislukt');
    } finally {
      setDeletingCashbookEntry(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <AppPageIntro
        eyebrow="Bank en kas"
        title="Transacties"
        description="Controleer hier wat er echt is gebeurd: imports, categorieën, interne transfers en je geselecteerde periode."
        badges={['Transactie-overzicht', 'CSV import', 'Kasboek']}
        icon={Wallet}
        action={
          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={() => fileInputRef.current?.click()}>
              CSV importeren
            </Button>
            <Button className="rounded-full" variant="outline" onClick={() => navigate('/dashboard')}>
              Naar dashboard
            </Button>
          </div>
        }
      />

      {showGettingStarted && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Begin met je eerste transacties
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">1. Voeg een bankrekening toe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Start in Instellingen met een rekeningnaam, bank en saldo.
              </p>
              <Button className="mt-4 w-full rounded-full" onClick={() => navigate('/settings')}>
                Naar Instellingen
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">2. Importeer een CSV</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload je transactiebestand zodat budgettoppers je uitgaven kan tonen en indelen.
              </p>
              <Button variant="outline" className="mt-4 w-full rounded-full" onClick={() => fileInputRef.current?.click()}>
                Kies bestand
              </Button>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">3. Controleer je eerste inzichten</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Na import zie je totalen, categorieen en je netto resultaat per periode.
              </p>
              <Button variant="outline" className="mt-4 w-full rounded-full" onClick={() => navigate('/dashboard')}>
                Naar Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-card border-primary/15">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="font-heading">CSV hulp</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Voor als je niet precies weet waar je moet beginnen met een transactiebestand.
              </p>
            </div>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setShowCsvHelper((value) => !value)}
            >
              {showCsvHelper ? 'Verberg uitleg' : 'Zo werkt CSV import'}
            </Button>
          </div>
        </CardHeader>
        {showCsvHelper && (
          <CardContent className="space-y-4 pt-0">
            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  step: '1',
                  title: 'Download een CSV bij je bank',
                  text: 'Zoek bij je bank naar woorden zoals transacties exporteren, afschrift downloaden of CSV.',
                },
                {
                  step: '2',
                  title: 'Kies hier je bestand',
                  text: 'Gebruik daarna de knop Bestand kiezen. Meestal hoef je verder niets aan het bestand te veranderen.',
                },
                {
                  step: '3',
                  title: 'Controleer je eerste overzicht',
                  text: 'Na de import zie je inkomsten, uitgaven en categorieen direct terug op deze pagina en op je dashboard.',
                },
              ].map((item) => (
                <div key={item.step} className="rounded-xl border bg-muted/20 p-4">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
                    {item.step}
                  </div>
                  <p className="font-medium">{item.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Handige tip: lukt het niet meteen? Probeer dan eerst een export van alleen je betaalrekening en een korte periode, bijvoorbeeld de laatste 3 maanden.
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Inkomsten in selectie</p>
            <p className="font-mono text-2xl font-bold text-green-600">
              +€{totalIncome.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Uitgaven in selectie</p>
            <p className="font-mono text-2xl font-bold text-red-600">
              -€{totalExpenses.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Netto mutatie in selectie</p>
            <p className={`font-mono text-2xl font-bold ${totalIncome - totalExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {totalIncome - totalExpenses >= 0 ? '+' : ''}€{(totalIncome - totalExpenses).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-card border-primary/15">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            Kasboek
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Voor contant geld, kasopnames vanaf je bank en contante uitgaven die je niet via je bank ziet.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">Kas saldo</p>
              <p className={`font-mono text-2xl font-bold ${cashbook.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {cashbook.balance >= 0 ? '+' : '-'}€{Math.abs(cashbook.balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">Beginsaldo contant</p>
              <p className="font-mono text-2xl font-bold">
                €{(cashbook.opening_balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">Contant ontvangen</p>
              <p className="font-mono text-2xl font-bold text-green-600">
                +€{(cashbook.income_total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">Vanaf bank naar kas</p>
              <p className="font-mono text-2xl font-bold text-blue-600">
                +€{(cashbook.transfer_in_total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground">Contant uitgegeven</p>
              <p className="font-mono text-2xl font-bold text-red-600">
                -€{(cashbook.expense_total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          <div className="rounded-xl border bg-blue-50 p-4">
            <p className="text-sm font-medium text-blue-900">Professionele kaslogica</p>
            <p className="mt-1 text-sm text-blue-800">
              Een geldopname bij een automaat is geen echte uitgave, maar een verplaatsing van bank naar contant geld.
              Pas je latere cash-uitgaven tellen mee als besteding.
            </p>
          </div>

          <div className="grid gap-3 rounded-xl border bg-background p-4 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <p className="text-sm font-medium">Contant beginsaldo</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Vul in hoeveel contant geld je nu echt hebt. Vanaf daar rekent het kasboek verder.
              </p>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Bijv. 80,00"
                value={cashOpeningBalance}
                onChange={(e) => setCashOpeningBalance(e.target.value)}
                className="mt-3 max-w-xs"
              />
            </div>
            <Button className="rounded-full md:min-w-44" onClick={saveCashbookProfile} disabled={savingCashProfile}>
              {savingCashProfile ? 'Opslaan...' : 'Beginsaldo opslaan'}
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-6">
            <Input
              type="date"
              value={cashbookForm.date}
              onChange={(e) => setCashbookForm((current) => ({ ...current, date: e.target.value }))}
            />
            <Input
              placeholder="Waar was dit voor?"
              value={cashbookForm.description}
              onChange={(e) => setCashbookForm((current) => ({ ...current, description: e.target.value }))}
              className="lg:col-span-2"
            />
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Bedrag"
              value={cashbookForm.amount}
              onChange={(e) => setCashbookForm((current) => ({ ...current, amount: e.target.value }))}
            />
            <Select
              value={cashbookForm.category}
              onValueChange={(value) => setCashbookForm((current) => ({ ...current, category: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                {cashbookCategories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={cashbookForm.entry_type}
              onValueChange={(value) => setCashbookForm((current) => ({ ...current, entry_type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Contante uitgave</SelectItem>
                <SelectItem value="income">Contante ontvangst</SelectItem>
                <SelectItem value="transfer_in">Kasopname vanaf bank</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground">
              Gebruik `Kasopname vanaf bank` als je pint bij een automaat. Dat verhoogt je kas, maar telt niet als gewone uitgave.
            </p>
            <Button className="rounded-full md:min-w-44" onClick={saveCashbookEntry} disabled={savingCashbook}>
              {savingCashbook ? 'Opslaan...' : 'Kasboekregel toevoegen'}
            </Button>
          </div>

          <div className="rounded-xl border">
            <div className="border-b px-4 py-3">
              <p className="font-medium">Recente kasboekregels</p>
            </div>
            {cashbook.items?.length ? (
              <div className="divide-y">
                {cashbook.items.slice(0, 6).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-medium">{entry.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString('nl-NL')} · {entry.entry_type === 'transfer_in' ? 'Kasopname vanaf bank' : entry.category}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-mono font-semibold ${entry.entry_type === 'expense' ? 'text-red-600' : entry.entry_type === 'transfer_in' ? 'text-blue-600' : 'text-green-600'}`}>
                        {entry.entry_type === 'expense' ? '-' : '+'}€{Math.abs(entry.amount).toFixed(2)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-full"
                        onClick={() => deleteCashbookEntry(entry.id)}
                        disabled={deletingCashbookEntry === entry.id}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-sm text-muted-foreground">
                Nog geen kasboekregels. Voeg hier je eerste contante uitgave of ontvangst toe.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="transactions-search"
                placeholder="Zoek transacties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={selectedPeriod}
              onValueChange={(value) => {
                setSelectedPeriod(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder="Periode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current_month">Deze maand</SelectItem>
                <SelectItem value="last_3_months">Laatste 3 maanden</SelectItem>
                <SelectItem value="all">Alles</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter" className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle categorieën</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category} value={cat.category}>
                    {cat.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="gap-2" onClick={exportTransactions}>
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" className="gap-2" onClick={rebuildCategories} disabled={rebuildingCategories || !hasTransactions}>
              <RefreshCcw className="w-4 h-4" />
              {rebuildingCategories ? 'Bezig met herindelen...' : 'Maak categorieen slimmer'}
            </Button>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className="sm:max-w-xs"
            />
            <Button type="button" onClick={importTransactions} disabled={importing}>
              {importing ? 'Importeren...' : 'Importeer CSV'}
            </Button>
          </div>
          {selectedFile && (
            <div className="mt-3 rounded-lg border border-primary/15 bg-primary/5 p-3 text-sm text-foreground">
              Gekozen bestand: <span className="font-medium">{selectedFile.name}</span>
            </div>
          )}
          {accounts.length === 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Voeg eerst een bankrekening toe in Instellingen voor de beste start van je transacties.
            </div>
          )}
          {!hasTransactions && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              Automatische bankkoppeling is nog niet algemeen beschikbaar. CSV-import is daarom nu de beste manier om direct te starten.
            </div>
          )}
          {hasTransactions && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Vind je bankindeling te grof of onlogisch? Gebruik <span className="font-medium">Maak categorieen slimmer</span> om je transacties opnieuw in te delen op basis van beschrijving en tegenpartij.
            </div>
          )}
          <div className="mt-3 text-sm text-muted-foreground">
            Je ziet nu de samenvatting voor <span className="font-medium">{selectedPeriodLabel}</span>.
          </div>
        </CardContent>
      </Card>

      {/* Transactions List / Table */}
      <Card className="shadow-card">
        <CardContent className="p-0">
          {!hasTransactions ? (
            <div className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <Wallet className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium">Nog geen transacties toegevoegd</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Upload je eerste CSV-bestand om je uitgaven en inkomsten hier te bekijken.
              </p>
            </div>
          ) : showFilteredEmptyState ? (
            <div className="p-8 text-center">
              <p className="font-medium">Geen transacties gevonden</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Pas je zoekterm of categorie aan om weer resultaten te zien.
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y md:hidden">
                {paginatedTransactions.map((tx) => (
                  <div key={tx.id} className="space-y-3 px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tx.is_income ? 'bg-green-100' : 'bg-red-100'}`}>
                          {tx.is_income ? (
                            <ArrowUpRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{getDisplayMerchant(tx)}</p>
                          <p className="mt-1 text-xs text-muted-foreground break-words">
                            {tx.description}
                          </p>
                        </div>
                      </div>
                      <span className={`shrink-0 font-mono text-sm font-semibold ${tx.is_income ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.is_income ? '+' : '-'}€{Math.abs(tx.amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{new Date(tx.date).toLocaleDateString('nl-NL')}</span>
                      <Badge variant="secondary" className={categoryColors[getDisplayCategory(tx.category)] || 'bg-gray-100 text-gray-700'}>
                        {getDisplayCategory(tx.category)}
                      </Badge>
                      {getDisplaySubcategory(tx) && (
                        <span>{getDisplaySubcategory(tx)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead>Beschrijving</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead className="text-right">Bedrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTransactions.map((tx) => (
                      <TableRow key={tx.id} className="table-row-hover">
                        <TableCell>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.is_income ? 'bg-green-100' : 'bg-red-100'}`}>
                            {tx.is_income ? (
                              <ArrowUpRight className="w-4 h-4 text-green-600" />
                            ) : (
                              <ArrowDownRight className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(tx.date).toLocaleDateString('nl-NL')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{getDisplayMerchant(tx)}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {tx.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={categoryColors[getDisplayCategory(tx.category)] || 'bg-gray-100 text-gray-700'}>
                            {getDisplayCategory(tx.category)}
                          </Badge>
                          {getDisplaySubcategory(tx) && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              {getDisplaySubcategory(tx)}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono font-semibold ${tx.is_income ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.is_income ? '+' : '-'}€{Math.abs(tx.amount).toFixed(2)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {filteredTransactions.length} transacties gevonden
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Pagina {page} van {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
